import React from "react";
import { Button } from "@heroui/react";
import { logError, getErrorMessage } from "../../utils/errorHandler";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    logError(error, "React Error Boundary", { errorInfo });

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI with vintage styling
      return (
        <div className="min-h-screen bg-gradient-to-br from-cream-50 to-sepia-100 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg border border-sepia-200 p-8 text-center">
            {/* Vintage-style error icon */}
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto bg-burnt-orange-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-burnt-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>

            {/* Error message */}
            <h2 className="text-2xl  font-bold text-sepia-800 mb-4">
              Oops! Something went wrong
            </h2>

            <p className="text-sepia-600 mb-6 leading-relaxed">
              {this.props.fallbackMessage ||
                "We encountered an unexpected error. Don't worry, our team has been notified and we're working to fix it."}
            </p>

            {/* Error details for development */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-sepia-500 hover:text-sepia-700 mb-2">
                  Technical Details (Development)
                </summary>
                <div className="bg-sepia-50 p-3 rounded border text-xs font-mono text-sepia-700 overflow-auto max-h-32">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={this.handleRetry}
                className="bg-olive-green-600 hover:bg-olive-green-700 text-white font-medium px-6 py-2 rounded-md transition-colors"
              >
                Try Again
              </Button>

              <Button
                onClick={this.handleReload}
                variant="bordered"
                className="border-sepia-300 text-sepia-700 hover:bg-sepia-50 font-medium px-6 py-2 rounded-md transition-colors"
              >
                Reload Page
              </Button>
            </div>

            {/* Support message */}
            <p className="text-xs text-sepia-500 mt-6">
              If this problem persists, please contact our support team.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = (Component, fallbackMessage) => {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary fallbackMessage={fallbackMessage}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

// Hook for error boundary in functional components
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const handleError = React.useCallback((error) => {
    logError(error, "useErrorHandler");
    setError(error);
  }, []);

  // Throw error to be caught by error boundary
  if (error) {
    throw error;
  }

  return { handleError, resetError };
};

export default ErrorBoundary;
